/**
 * locations-v3.js
 * Custom elements:
 *   <locations-v3-section>    — store locator V3 (A/B variant)
 *   <location-pref-button-v3> — preferred clinic toggle button
 *
 * Business logic is a faithful port from locations-v2.js. All original behaviour
 * is preserved:
 *   - API pagination (results + totalPages)
 *   - Consortium-specific booking URLs / webSchedulerEnabled
 *   - Preferred clinic API (GET + POST via LRTokenKey)
 *   - 50 km radius filtering from map centre
 *   - UAT hostname rewrites + BonLook coordinate overrides
 *   - mapsPlace localStorage pre-population
 *   - centre_changed re-render (debounced)
 *   - handleOptionalRedirect after preferred clinic save
 *   - appendUtmSource / appendCurrentParams on outbound links
 *
 * Presentation changes vs V2:
 *   - Custom element name:   locations-v3-section
 *   - Pref button element:   location-pref-button-v3
 *   - CSS class prefix:      lv3__*
 *   - Data attributes:       data-lv3-*
 *   - Layout: two-column hero (no search row), narrower single-column list
 *   - Cards: address+distance inline, hours row, availability badge, closest badge
 *   - Toggle pill: mobile-only floating, not in hero
 *   - SEO copy: mobile expander accordion
 *   - Geolocation: manual "Use my current location" trigger
 *   - Map: area label overlay, mobile selected-clinic card
 *
 * Do NOT replace locations.js or locations-v2.js — this is an A/B test variant.
 */

(() => {
  if (typeof window.customElements.get('locations-v3-section') !== 'undefined') { return; }

  window.customElements.define('locations-v3-section', class LocationsV3Section extends HTMLElement {

    constructor() {
      super();

      // DOM refs
      this.list           = this.querySelector('[data-lv3-list]');
      this.listWrapper    = this.querySelector('[data-lv3-list-wrapper]');
      this.mapEl          = this.querySelector('[data-lv3-map]');
      this.mapWrapper     = this.querySelector('[data-lv3-map-wrapper]');
      this.mapLabel       = this.querySelector('[data-lv3-map-label]');
      this.mapSelected    = this.querySelector('[data-lv3-map-selected]');
      this.body           = this.querySelector('[data-lv3-body]');
      this.toggleLists    = this.querySelectorAll('[data-toggle-list]');
      this.toggleMaps     = this.querySelectorAll('[data-toggle-map]');
      this.resultsCount   = this.querySelector('[data-lv3-results-count]');
      this.submit         = this.querySelector('[data-lv3-submit]');
      this.form           = this.querySelector('[data-lv3-form]');
      this.input          = this.querySelector('[data-lv3-input]');
      this.geoTrigger     = this.querySelector('[data-lv3-geo-trigger]');
      this.seoTrigger     = this.querySelector('[data-lv3-seo-trigger]');
      this.seoContent     = this.querySelector('[data-lv3-seo-content]');

      // State — identical to V2
      this.clickTime              = Date.now() - 1001;
      this.visiqueHiddenBookingIds = ['508'];
      this.allLocations           = [];
      this.markers                = [];
      this.map                    = null;
      this.range                  = 50000;   // 50 km radius
      this.autocomplete           = null;
      this.infowindow             = null;
      this.defaultAddress         = window.locations.default_location;
      this.loc                    = [];
      this.attempts               = 0;
      this.prefLocation           = 0;
      this.locationSettled        = false;   // gates availability API calls until we know the user's area

      this.initToggles();
      this.initSeoExpander();
      this.initGeoTrigger();
      this.init();
      this.checkToken();
      this.measureHero();
    }

    // ------------------------------------------------------------------
    // Measure hero height for body height calculation
    // ------------------------------------------------------------------
    measureHero() {
      const hero = this.querySelector('.lv3__hero');
      if (!hero) { return; }
      const updateHeight = () => {
        this.style.setProperty('--lv3-hero-h', hero.offsetHeight + 'px');
      };
      updateHeight();
      if (window.ResizeObserver) {
        new ResizeObserver(updateHeight).observe(hero);
      }
    }

    // ------------------------------------------------------------------
    // Init
    // ------------------------------------------------------------------
    async init() {
      this.allLocations = await this.getLocations();
      if (!this.allLocations) { return; }

      await this.initMap();

      // A search handoff from another page wins over auto-geolocation.
      const mapsPlaceRaw = localStorage.getItem('mapsPlace');

      // Best centre we can pick synchronously (a cached fix from a previous
      // visit) so the very first paint is already at the user's area — no
      // New Westminster → user "flip", and no wasted default render.
      if (!mapsPlaceRaw) { this.applyInitialCenter(); }

      // First paint. Availability API calls only fire once locationSettled is
      // true (see renderListLocation), so we never fire the throwaway batch
      // for the default location before geolocation resolves.
      this.renderMarkers();
      this.renderListLocation();

      // Attach the pan-to-refilter listener only AFTER the initial centring,
      // so the programmatic setCenter calls above don't trigger extra renders.
      this.map.addListener('center_changed', () => {
        if (Date.now() > (this.clickTime + 1000)) {
          this.renderMarkers();
          this.renderListLocation();
        }
      });

      if (mapsPlaceRaw) {
        const mapsPlace = JSON.parse(mapsPlaceRaw);
        this.updateCenter(mapsPlace);
        this.input.value = mapsPlace.name;
        localStorage.removeItem('mapsPlace');
      } else {
        // Resolve live geolocation in the background; when it returns we settle
        // and render once, at the real location.
        this.resolveGeolocation();
      }

      // Inline message element for search errors (no results, API failure)
      this.searchMsg = document.createElement('span');
      this.searchMsg.className = 'lv3__search-msg';
      this.searchMsg.setAttribute('aria-live', 'polite');
      this.form.insertAdjacentElement('afterend', this.searchMsg);

      this.autocomplete = new google.maps.places.Autocomplete(this.input);
      this.autocomplete.setComponentRestrictions({ country: ['ca'] });
      this.autocomplete.addListener('place_changed', this.updateCenter.bind(this));

      this.form.addEventListener('submit', (event) => {
        event.preventDefault();
        this.submitInput();
      });
    }

    // ------------------------------------------------------------------
    // Search: Places AutocompleteService fallback
    // ------------------------------------------------------------------
    submitInput() {
      if (!this.input.value || this.input.value === '') { return; }
      // NOTE: do NOT call updateCenter() here — if the user typed without selecting
      // an autocomplete suggestion, getPlace() returns a place with no geometry and
      // updateCenter() would throw, aborting everything before the Places API runs.
      // The getPlacePredictions → getDetails → updateCenter(place) path below
      // handles all free-text searches correctly.

      // Swap button content for bouncing dots while the Places API resolves
      const originalBtnHTML = this.submit ? this.submit.innerHTML : '';
      if (this.submit) {
        this.submit.innerHTML = '<span class="lv3__submit-dots" aria-label="Searching"><span></span><span></span><span></span></span>';
        this.submit.classList.add('lv3__submit-btn--loading');
      }
      const resetSubmit = () => {
        if (this.submit) {
          this.submit.innerHTML = originalBtnHTML;
          this.submit.classList.remove('lv3__submit-btn--loading');
        }
      };

      this.placesService = new google.maps.places.PlacesService(document.createElement('div'));
      this.autocompleteService = new google.maps.places.AutocompleteService();

      const request = {
        input: this.input.value,
        componentRestrictions: { country: 'ca' }
      };

      this.autocompleteService.getPlacePredictions(request, (predictions, status) => {
        if (status !== 'OK') {
          resetSubmit();
          this.showSearchMsg(window.locations.text.search_no_results);
          return;
        }

        this.placesService.getDetails({ placeId: predictions[0].place_id }, (place, detailStatus) => {
          resetSubmit();
          if (detailStatus !== google.maps.places.PlacesServiceStatus.OK) {
            this.showSearchMsg(window.locations.text.search_load_error);
            return;
          }
          this.clearSearchMsg();
          this.updateCenter(place);
        });
      });
    }

    showSearchMsg(text) {
      if (!this.searchMsg) { return; }
      this.searchMsg.textContent = text;
      clearTimeout(this._searchMsgTimer);
      this._searchMsgTimer = setTimeout(() => this.clearSearchMsg(), 6000);
    }

    clearSearchMsg() {
      if (this.searchMsg) { this.searchMsg.textContent = ''; }
      clearTimeout(this._searchMsgTimer);
    }

    // ------------------------------------------------------------------
    // SEO expander (mobile)
    // ------------------------------------------------------------------
    initSeoExpander() {
      if (!this.seoTrigger || !this.seoContent) { return; }
      this.seoTrigger.addEventListener('click', () => {
        const expanded = this.seoTrigger.getAttribute('aria-expanded') === 'true';
        this.seoTrigger.setAttribute('aria-expanded', String(!expanded));
        this.seoContent.classList.toggle('lv3__seo-content--open', !expanded);
      });
    }

    // ------------------------------------------------------------------
    // "Use my current location" manual trigger
    // ------------------------------------------------------------------
    initGeoTrigger() {
      if (!this.geoTrigger) { return; }

      // Inline message element — shown on blocked / error states
      this.geoMsg = document.createElement('span');
      this.geoMsg.className = 'lv3__geo-msg';
      this.geoMsg.setAttribute('aria-live', 'polite');
      this.geoTrigger.insertAdjacentElement('afterend', this.geoMsg);

      const GEO_SVG = `<svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true" xmlns="http://www.w3.org/2000/svg"><circle cx="8" cy="8" r="2.5" stroke="currentColor" stroke-width="1.4"/><path d="M8 1v2M8 13v2M1 8h2M13 8h2" stroke="currentColor" stroke-width="1.4" stroke-linecap="round"/><circle cx="8" cy="8" r="6" stroke="currentColor" stroke-width="1.4"/></svg>`;

      const resetBtn  = () => {
        this.geoTrigger.disabled = false;
        this.geoTrigger.innerHTML = `${GEO_SVG} ${window.locations.text.use_my_location}`;
      };
      const showMsg = (text) => {
        this.geoMsg.textContent = text;
        // Auto-clear after 8 s
        clearTimeout(this._geoMsgTimer);
        this._geoMsgTimer = setTimeout(() => { this.geoMsg.textContent = ''; }, 8000);
      };
      const clearMsg = () => { this.geoMsg.textContent = ''; };

      const doLocate = () => {
        clearMsg();
        this.geoTrigger.disabled = true;
        this.geoTrigger.textContent = window.locations.text.locating;
        navigator.geolocation.getCurrentPosition(
          (pos) => {
            resetBtn();
            // Clear the prior search term — the results now reflect the user's
            // actual location, not whatever was last typed in the box.
            if (this.input) { this.input.value = ''; }
            this.setPosition(pos);
          },
          (err) => {
            resetBtn();
            if (err.code === 1) {
              showMsg(window.locations.text.location_blocked);
            }
          },
          { enableHighAccuracy: false, maximumAge: 30000, timeout: 15000 }
        );
      };

      this.geoTrigger.addEventListener('click', () => {
        if (!navigator.geolocation) {
          showMsg(window.locations.text.location_not_supported);
          return;
        }

        // Pre-check permission state so we can give immediate feedback for
        // 'denied' without the button ever flashing "Locating…"
        if (navigator.permissions) {
          navigator.permissions.query({ name: 'geolocation' }).then((result) => {
            if (result.state === 'denied') {
              showMsg(window.locations.text.location_blocked);
            } else {
              doLocate(); // 'granted' or 'prompt' — browser will ask if needed
            }
          }).catch(() => doLocate()); // permissions API unavailable, just try
        } else {
          doLocate();
        }
      });
    }

    // ------------------------------------------------------------------
    // Mobile/desktop view toggle
    // ------------------------------------------------------------------
    initToggles() {
      this.toggleLists.forEach(btn => {
        btn.addEventListener('click', () => this.setView('list'));
      });
      this.toggleMaps.forEach(btn => {
        btn.addEventListener('click', () => this.setView('map'));
      });
    }

    setView(type) {
      if (this.body) {
        this.body.classList.toggle('lv3__body--map', type === 'map');
      }

      this.toggleLists.forEach(btn => {
        btn.classList.toggle('lv3__toggle-btn--active', type === 'list');
        btn.setAttribute('aria-pressed', type === 'list' ? 'true' : 'false');
      });
      this.toggleMaps.forEach(btn => {
        btn.classList.toggle('lv3__toggle-btn--active', type === 'map');
        btn.setAttribute('aria-pressed', type === 'map' ? 'true' : 'false');
      });

      if (type === 'map' && this.map) {
        setTimeout(() => {
          google.maps.event.trigger(this.map, 'resize');
          if (this.loc && this.loc.lat) { this.map.setCenter(this.loc); }
        }, 50);
      }

      // Hide selected card when switching to list view
      if (type === 'list' && this.mapSelected) {
        this.mapSelected.classList.remove('lv3__map-selected--visible');
      }
    }

    // ------------------------------------------------------------------
    // Preferred clinic token check
    // ------------------------------------------------------------------
    checkToken() {
      if (!window.locations.is_customer) { return; }

      const tokenString = localStorage.getItem('LRTokenKey');
      if (tokenString) {
        this.fetchPreferedClinic(tokenString);
      } else if (this.attempts < 10) {
        this.attempts++;
        setTimeout(() => this.checkToken(), 300);
      }
    }

    async fetchPreferedClinic(tokenString) {
      if (!localStorage.getItem('pc') || localStorage.getItem('pc') === 'undefined') {
        await fetch(`${window.locations.api_url}/customer/preferred-clinic`, {
          method: 'GET',
          headers: { 'Authorization': 'Bearer ' + tokenString }
        })
          .then(response => response.status === 200 ? response.json() : null)
          .then(data => {
            if (!data) { return; }
            this.prefLocation = JSON.parse(data.locationId);
          })
          .catch(error => console.error(error));
      } else {
        this.prefLocation = localStorage.getItem('pc');
      }
    }

    // ------------------------------------------------------------------
    // Fetch locations (paginated)
    // ------------------------------------------------------------------
    async getLocations() {
      try {
        const baseUrl = `${window.locations.api_url}/locations/paginated?lang=${window.locations.language}&NbPerPage=200`;

        const firstResponse = await fetch(`${baseUrl}&PageNb=1`);
        if (!firstResponse.ok) { throw new Error('Failed to fetch locations'); }
        const firstData = await firstResponse.json();
        let allResults = firstData.results;

        if (firstData.totalPages > 1) {
          const remainingPages = Array.from({ length: firstData.totalPages - 1 }, (_, i) => i + 2);
          const responses = await Promise.all(
            remainingPages.map(pageNb =>
              fetch(`${baseUrl}&PageNb=${pageNb}`).then(res => {
                if (!res.ok) { throw new Error('Failed to fetch locations'); }
                return res.json();
              })
            )
          );
          responses.forEach(data => { allResults = allResults.concat(data.results); });
        }

        // UAT: hard-coded coordinates for BonLook locations
        if (window.location.hostname.includes('web.uat.fyidoctors.com')) {
          const coordinateOverrides = {
            4531: { latitude: 48.4279, longitude: -71.0550 },
            4532: { latitude: 45.3783, longitude: -71.8465 },
            4533: { latitude: 46.7812, longitude: -71.2768 },
            4534: { latitude: 45.4765, longitude: -75.7013 },
            4535: { latitude: 45.5032, longitude: -73.5735 },
            4536: { latitude: 45.5245, longitude: -73.6005 },
            4537: { latitude: 45.4835, longitude: -73.5815 },
            604:  { latitude: 45.4451, longitude: -73.4692 },
            605:  { latitude: 45.5025, longitude: -73.5725 },
            606:  { latitude: 45.5840, longitude: -73.5430 },
            607:  { latitude: 46.3575, longitude: -72.5742 },
          };
          allResults.forEach(location => {
            if (coordinateOverrides[location.externalId]) {
              if (!location.latitude && !location.longitude) {
                location.latitude  = coordinateOverrides[location.externalId].latitude;
                location.longitude = coordinateOverrides[location.externalId].longitude;
              }
              if (!location.name) { location.name = 'BonLook'; }
            }
          });
        }

        return allResults.filter(location =>
          location.status?.toLowerCase().includes('closed') === false
        );
      } catch (error) {
        console.error(error);
      }
    }

    // ------------------------------------------------------------------
    // Geolocation
    // ------------------------------------------------------------------
    // Resolve the visitor's live position in the background. Availability
    // calls are gated on locationSettled, so nothing expensive fires for the
    // default location before this resolves.
    resolveGeolocation() {
      if (!navigator.geolocation) { this.settleDefault(); return; }

      const runFix = () => {
        navigator.geolocation.getCurrentPosition(
          this.setPosition.bind(this),
          // Denied/failed at the prompt — settle on the current centre so
          // availability can still load.
          () => { this.settleDefault(); },
          { enableHighAccuracy: false, maximumAge: 30000, timeout: 20000 }
        );
      };

      if (!navigator.permissions) { runFix(); return; }

      navigator.permissions.query({ name: 'geolocation' }).then((result) => {
        if (result.state === 'denied') {
          this.settleDefault();
        } else {
          // 'granted' or 'prompt' — browser resolves (asking if needed).
          runFix();
        }
        result.onchange = () => { this.report(result.state); };
      }).catch(() => runFix());
    }

    // No live fix available — mark the current centre (cached fix or default)
    // as settled so availability can load. Skips a redundant render if we were
    // already settled from a cached fix.
    settleDefault() {
      localStorage.setItem('geoperm', 'false');
      if (this.locationSettled) { return; }
      this.locationSettled = true;
      this.renderListLocation();
    }

    // Centre the map on a cached fix from a previous visit, if we have a fresh
    // one. Runs before the first paint and before the center_changed listener
    // is attached, so it does not trigger an extra render.
    applyInitialCenter() {
      const cached = this.readCachedGeo();
      if (!cached) { return; }
      this.loc              = cached;
      this.customerLocation = cached;
      this.defaultAddress   = cached;
      this.map.setCenter(cached);
      this.locationSettled  = true;
    }

    readCachedGeo() {
      try {
        const raw = localStorage.getItem('lv3_last_geo');
        if (!raw) { return null; }
        const { lat, lng, ts } = JSON.parse(raw);
        if (typeof lat !== 'number' || typeof lng !== 'number') { return null; }
        // 36-hour freshness — a recent fix is still a far better first paint
        // than defaulting to New Westminster, but we don't want to centre on a
        // stale location if the visitor has since travelled.
        if (Date.now() - ts > 36 * 60 * 60 * 1000) { return null; }
        return { lat, lng };
      } catch (e) {
        return null;
      }
    }

    writeCachedGeo(loc) {
      try {
        localStorage.setItem('lv3_last_geo', JSON.stringify({ lat: loc.lat, lng: loc.lng, ts: Date.now() }));
      } catch (e) { /* storage unavailable */ }
    }

    report(state) {
      console.log('Permission: ' + state);
    }

    setPosition(geolocationPosition) {
      if (geolocationPosition.coords) {
        localStorage.setItem('geoperm', 'true');
        this.loc = {
          lat: geolocationPosition.coords.latitude,
          lng: geolocationPosition.coords.longitude
        };
        this.defaultAddress = this.loc;
        // Cache the fix so the next visit paints at the right area instantly.
        this.writeCachedGeo(this.loc);
      } else {
        localStorage.setItem('geoperm', 'false');
      }
      // Guard: only proceed if we have a valid location and an initialised map
      if (!this.map || !this.loc || !this.loc.lat) { return; }
      this.locationSettled = true;
      this.customerLocation = this.loc;
      this.map.setCenter(this.loc);
      this.renderListLocation();
      this.renderMarkers();
      // Scroll the list back to the top so the user sees the updated results
      if (this.listWrapper) { this.listWrapper.scrollTop = 0; }
    }

    updateCenter(data = this.autocomplete?.getPlace()) {
      if (!data || !data.geometry || !data.geometry.location) { return; } // no valid place — nothing to pan to
      // location may be a live google.maps.LatLng (lat()/lng() methods) or a
      // plain {lat, lng} literal restored from localStorage (mapsPlace handoff).
      const loc = data.geometry.location;
      const lat = typeof loc.lat === 'function' ? loc.lat() : loc.lat;
      const lng = typeof loc.lng === 'function' ? loc.lng() : loc.lng;
      this.loc = { lat, lng };
      this.locationSettled = true;   // an explicit place was chosen — load availability
      // Use setCenter, NOT panTo. panTo is animated and does not synchronously
      // update map.getCenter() — so filterLocations() (called immediately below)
      // would still read the old map centre and return the wrong clinics.
      // setCenter updates getCenter() before the next line executes.
      this.map.setCenter({ lat, lng });
      this.renderMarkers();
      this.renderListLocation();
      // Scroll list to top so the closest results are immediately visible
      if (this.listWrapper) { this.listWrapper.scrollTop = 0; }
    }

    // ------------------------------------------------------------------
    // Map init
    // ------------------------------------------------------------------
    async initMap(locationCenter = window.locations.default_location) {
      this.map = new google.maps.Map(this.mapEl, {
        center: locationCenter,
        zoom: 10,
        mapTypeControl: false,
        streetViewControl: false,
        fullscreenControl: false,
      });

      try {
        const res = await fetch(window.locations.map_json_url);
        if (!res.ok) { throw new Error(res.statusText); }
        const data = await res.json();
        this.map.setOptions({ styles: data });
      } catch (err) {
        console.error(err);
      }
      // NOTE: the center_changed listener is attached in init() AFTER the
      // initial centring, so programmatic setCenter calls during boot don't
      // trigger redundant renders.
    }

    // ------------------------------------------------------------------
    // Markers
    // ------------------------------------------------------------------
    renderMarkers() {
      this.markers.forEach(marker => marker.setMap(null));
      this.markers = [];
      const locations = this.filterLocations();

      locations.forEach((item, index) => {
        const marker = new google.maps.Marker({
          position: { lat: item.latitude, lng: item.longitude },
          icon: window.locations.marker_svg_url,
          map: this.map
        });

        google.maps.event.addListener(marker, 'click', ((marker, item, idx) => {
          return () => {
            if (this.infowindow) { this.infowindow.close(); }
            this.infowindow = new google.maps.InfoWindow();

            const currentLocation = this.loc && this.loc.lat ? this.loc : window.locations.default_location;
            const dist = this.renderDistance(item);
            const distLabel = dist ? ` · ${parseInt(this.calcCrow(currentLocation.lat, currentLocation.lng, item.latitude, item.longitude))} km` : '';
            const labelText = idx === 0 ? `${window.locations.text.closest_to_you}${distLabel}` : (dist ? dist.replace('km', ' km') : '');

            const popupPhone = item.phoneNumber
              ? `<a href="tel:${item.phoneNumber.replace(/\D/g, '')}" class="lv3-popup__phone">${item.phoneNumber}</a>`
              : '';
            const infoWindowContent = `
              <div class="lv3-popup">
                <div class="lv3-popup__label">${labelText}</div>
                <div class="lv3-popup__name">${item.name}</div>
                <div class="lv3-popup__address">${item.addressLine1}</div>
                ${popupPhone}
                <div class="lv3-popup__actions">
                  ${this.renderBookingButton(item, true)}
                  ${this.renderDetailsButton(item, true)}
                </div>
              </div>
            `;
            this.infowindow.setContent(infoWindowContent);
            this.infowindow.open(this.map, marker);
            this.clickTime = Date.now();

            // Mobile: also show bottom card
            this.renderMapSelectedCard(item, idx, distLabel);
          };
        })(marker, item, index));

        this.markers.push(marker);
      });
    }

    // ------------------------------------------------------------------
    // Mobile map selected-clinic bottom card
    // ------------------------------------------------------------------
    renderMapSelectedCard(item, index, distLabel) {
      if (!this.mapSelected) { return; }
      const labelText = index === 0 ? `${window.locations.text.closest_to_you}${distLabel}` : (distLabel || '');
      const selectedPhone = item.phoneNumber
        ? `<a href="tel:${item.phoneNumber.replace(/\D/g, '')}" class="lv3__map-selected-phone">${item.phoneNumber}</a>`
        : '';
      this.mapSelected.innerHTML = `
        <div class="lv3__map-selected-label">${labelText}</div>
        <div class="lv3__map-selected-name">${item.name}</div>
        <div class="lv3__map-selected-address">${item.addressLine1}</div>
        ${selectedPhone}
        <div class="lv3__map-selected-actions">
          ${this.renderBookingButton(item, true)}
          ${this.renderDetailsButton(item, true)}
        </div>
      `;
      this.mapSelected.classList.add('lv3__map-selected--visible');
    }

    // ------------------------------------------------------------------
    // List render
    // ------------------------------------------------------------------
    renderListLocation() {
      const locations = this.filterLocations();
      this.list.innerHTML = '';

      const count = locations.length;
      this.resultsCount.innerHTML = `${count} ${count === 1 ? window.locations.text.result_singular : window.locations.text.result_plural}`;

      // Update map area label
      this.updateMapLabel(locations);

      if (locations && locations.length) {
        const currentLocation = this.loc && this.loc.lat ? this.loc : window.locations.default_location;
        locations.sort((a, b) => {
          const aDistance = this.calcCrow(currentLocation.lat, currentLocation.lng, a.latitude, a.longitude);
          const bDistance = this.calcCrow(currentLocation.lat, currentLocation.lng, b.latitude, b.longitude);
          if (a.externalId === Number(this.prefLocation)) { return -1; }
          if (b.externalId === Number(this.prefLocation)) { return 1; }
          return aDistance - bDistance;
        });

        locations.forEach((item, index) => {
          try {
            const locationHtml = `
              <li class="lv3__location ${item.externalId === Number(this.prefLocation) ? 'lv3__location--preferred' : ''}"
                  data-lv3-location
                  data-loc-id="${item.externalId}">
                ${index === 0 ? `<span class="lv3__closest-badge">${window.locations.text.closest_to_you}</span>` : ''}
                ${this.renderTitle(item)}
                ${this.renderAddressRow(item)}
                ${this.renderPhoneRow(item)}
                ${this.renderHoursRow(item)}
                ${this.renderAvailabilitySlot(item)}
                <div data-lv3-pref-status>
                  ${this.renderPreferredClinic(item)}
                  ${this.renderMakePreferred(item)}
                </div>
                <div class="lv3__location-actions">
                  ${this.renderBookingButton(item)}
                  ${this.renderDetailsButton(item)}
                </div>
              </li>
            `;
            this.list.innerHTML += locationHtml;
          } catch (e) {
            console.error('Error rendering location:', item.externalId, e);
          }
        });

        // Lazy-load availability badges after the main list is painted — but
        // only once we know the user's area. Before geolocation settles, the
        // list is showing the default (New Westminster) clinics; firing the
        // slots API for those just to discard the results is the wasted work
        // this variant avoids.
        if (this.locationSettled) {
          const loadFn = () => this.loadAvailability(locations);
          if (typeof requestIdleCallback !== 'undefined') {
            requestIdleCallback(loadFn, { timeout: 2000 });
          } else {
            setTimeout(loadFn, 100);
          }
        }

      } else {
        this.list.innerHTML = `
          <li class="lv3__location lv3__location--no-results">
            <div class="lv3__location-name">${window.locations.text.no_locations}</div>
          </li>
        `;
      }
    }

    // ------------------------------------------------------------------
    // Map area label: "Greater Vancouver · 5 clinics"
    // ------------------------------------------------------------------
    updateMapLabel(locations) {
      if (!this.mapLabel || !this.map) { return; }

      if (locations.length === 0) {
        this.mapLabel.classList.remove('lv3__map-area-label--visible');
        return;
      }

      const center = this.map.getCenter();
      if (!center) { return; }

      const geocoder = new google.maps.Geocoder();
      geocoder.geocode({ location: { lat: center.lat(), lng: center.lng() } }, (results, status) => {
        if (status !== 'OK' || !results[0]) {
          this.mapLabel.textContent = `${locations.length} clinic${locations.length === 1 ? '' : 's'}`;
          this.mapLabel.classList.add('lv3__map-area-label--visible');
          return;
        }
        const locality = results.find(r => r.types.includes('locality') || r.types.includes('administrative_area_level_2'));
        const areaName = locality ? locality.address_components[0].long_name : '';
        const label = areaName
          ? `${areaName} · ${locations.length} clinic${locations.length === 1 ? '' : 's'}`
          : `${locations.length} clinic${locations.length === 1 ? '' : 's'}`;
        this.mapLabel.textContent = label;
        this.mapLabel.classList.add('lv3__map-area-label--visible');
      });
    }

    // ------------------------------------------------------------------
    // URL helpers
    // ------------------------------------------------------------------

    // Read the Shopify locale from window.locations.language (set in Liquid
    // from request.locale.iso_code) — more reliable than parsing pathname.
    currentLocale() {
      return (window.locations.language || 'en').toLowerCase().startsWith('fr') ? 'fr' : 'en';
    }

    // Return the correct clinic page URL for the current locale.
    //
    // FYidoctors rule: ALWAYS use webpageEn and strip any /fr/ path segment.
    // FYi has no working French page variants — /fr/ in the URL causes 404s.
    //
    // Visique / other brands: pick webpageFr on French locale, webpageEn otherwise,
    // with a cross-language fallback so null values never produce a broken link.
    getPageUrl(item) {
      const isFyi = item.consortium && item.consortium.toUpperCase() === 'FYIDOCTORS';

      if (isFyi) {
        const url = item.webpageEn || item.webpageFr || null;
        if (!url) { return null; }
        // Strip any /fr/ path segment that may be present in the API data
        try {
          const u = new URL(url);
          u.pathname = u.pathname.replace(/^\/fr\//, '/');
          return u.toString();
        } catch (e) {
          return url.replace(/\/fr\//, '/');
        }
      }

      // Non-FYi (Visique etc.) — locale-aware with fallback
      const lang = this.currentLocale();
      return lang === 'fr'
        ? (item.webpageFr || item.webpageEn || null)
        : (item.webpageEn || item.webpageFr || null);
    }

    // ------------------------------------------------------------------
    // Render helpers
    // ------------------------------------------------------------------
    renderTitle(item) {
      const webpage = this.getPageUrl(item);

      if (
        window.location.host === 'web.uat.fyidoctors.com' ||
        window.location.host === 'visique-dev.myshopify.com'
      ) {
        try {
          const { pathname, host } = new URL(webpage);
          const newHost = host === 'visique.com' ? 'visique-dev.myshopify.com' : 'web.uat.fyidoctors.com';
          const newUrl = `https://${newHost}${pathname}`;
          return `<h2 class="lv3__location-name" data-id="${item.name}"><a href="${newUrl}">${item.name}</a></h2>`;
        } catch (e) {
          // fall through
        }
      }

      if (!webpage) {
        return `<h2 class="lv3__location-name" data-id="${item.name}">${item.name}</h2>`;
      }
      return `<h2 class="lv3__location-name" data-id="${item.name}"><a href="${webpage}">${item.name}</a></h2>`;      

    }

    renderPhoneRow(item) {
      if (!item.phoneNumber) { return ''; }
      const digits = item.phoneNumber.replace(/\D/g, '');
      if (!digits) { return ''; }
      return `
        <div class="lv3__phone-row">
          <svg width="13" height="13" viewBox="0 0 13 13" fill="none" aria-hidden="true" xmlns="http://www.w3.org/2000/svg">
            <path d="M11.5 8.83c-.84-.04-1.66-.18-2.44-.41a.91.91 0 0 0-.93.21l-1.5 1.5A8.79 8.79 0 0 1 2.87 6.37l1.5-1.5A.91.91 0 0 0 4.59 4 8.72 8.72 0 0 1 4.17 1.5C4.11 1 3.67.5 3.17.5H1.17c-.58 0-1.04.5-1.01 1.07A10.37 10.37 0 0 0 11.43 12.84c.57.03 1.07-.43 1.07-1.01V9.83c0-.52-.42-.97-.97-1Z" stroke="currentColor" stroke-width="1.2" stroke-linejoin="round"/>
          </svg>
          <a href="tel:${digits}" class="lv3__phone-link">${item.phoneNumber}</a>
        </div>
      `;
    }

    renderAddressRow(item) {
      const dist = this.renderDistanceText(item);
      const address = [item.addressLine1, item.city, item.province].filter(Boolean).join(', ');
      return `
        <div class="lv3__addr-row">
          <span class="lv3__addr-pin">
            <svg width="13" height="14" viewBox="0 0 13 14" fill="none" aria-hidden="true" xmlns="http://www.w3.org/2000/svg">
              <path d="M6.5 1C4.29 1 2.5 2.79 2.5 5c0 3.25 4 8 4 8s4-4.75 4-8c0-2.21-1.79-4-4-4Z" stroke="currentColor" stroke-width="1.3" stroke-linejoin="round"/>
              <circle cx="6.5" cy="5" r="1.5" stroke="currentColor" stroke-width="1.3"/>
            </svg>
          </span>
          <span class="lv3__location-address">${address}</span>
          ${dist ? `<span class="lv3__location-dist">${dist}</span>` : ''}
        </div>
      `;
    }

    renderDistanceText(item) {
      if (localStorage.getItem('geoperm') === 'false') { return ''; }
      if (!this.loc || !this.loc.lat) { return ''; }
      const dist = this.calcCrow(this.loc.lat, this.loc.lng, item.latitude, item.longitude);
      if (isNaN(Number(dist))) { return ''; }
      return `${parseInt(dist)} km`;
    }

    // Kept for backwards compatibility (used in marker click handler)
    renderDistance(item) {
      return this.renderDistanceText(item);
    }

    renderHoursRow(item) {
      const display = item.displayHours || item.todayHours || null;
      if (!display) { return ''; }
      const isOpen = item.isCurrentlyOpen ?? display.toLowerCase().includes('open until');
      return `
        <div class="lv3__hours-row">
          <span class="lv3__hours-dot ${isOpen ? 'lv3__hours-dot--open' : 'lv3__hours-dot--closed'}"></span>
          <span>${display}</span>
        </div>
      `;
    }

    // Decides whether to render the avail slot at all, and what initial content.
    //
    // Show slot when:
    //   • Any FYi clinic           — phone-only badge (no scheduler) OR pulsing → availability
    //   • Any web-scheduler clinic — pulsing → availability (covers Visique + web scheduler)
    //
    // Skip slot when:
    //   • Visique (or other brand) without web scheduler — they have a different
    //     booking path but no scheduler-based availability to display
    renderAvailabilitySlot(item) {
      const isFyi     = item.consortium && item.consortium.toUpperCase() === 'FYIDOCTORS';
      const enabled   = item.webSchedulerEnabled;

      // No slot needed for non-FYi clinics without a web scheduler
      if (!isFyi && !enabled) { return ''; }

      const badge = (isFyi && !enabled)
        ? `<span class="lv3__avail-badge lv3__avail-badge--phone">${window.locations.text.avail_phone_only}</span>`
        : `<span class="lv3__avail-placeholder" aria-hidden="true"></span>`;

      return `
        <div class="lv3__avail-slot" data-lv3-avail="${item.externalId}">
          ${badge}
        </div>`;
    }

    // Kept as a thin wrapper in case it's called from other paths
    renderAvailabilityBadge(item) {
      return this.renderAvailabilitySlot(item);
    }

    // ------------------------------------------------------------------
    // Lazy availability loading
    // Fires after the list is painted. Checks sessionStorage cache first,
    // then fetches in batches of 5 to avoid hammering the API.
    // ------------------------------------------------------------------
    async loadAvailability(locations) {
      // All web-scheduler-enabled clinics use the same /locations/{id}/slots API
      // regardless of brand (FYi AND Visique web-scheduler clinics)
      const eligible = locations.filter(item => item.webSchedulerEnabled);
      if (!eligible.length) { return; }

      // Sequential — one request at a time to avoid 500 errors
      for (const item of eligible) {
        await this.checkAvailability(item.externalId);
      }
    }

    // Fetch (or restore from cache) and render availability for one clinic.
    async checkAvailability(locationId) {
      const CACHE_KEY = `lv3_avail_v3_${locationId}`; // v3: sort date groups ascending
      // 2 hours — the badge is coarse (this week / next week / 2+ weeks), so it
      // doesn't need to be minute-fresh. This keeps panning around the map from
      // re-hitting the slots API for clinics we've already checked this session.
      const CACHE_TTL = 2 * 60 * 60 * 1000; // 2 hours

      // Cache hit
      try {
        const raw = sessionStorage.getItem(CACHE_KEY);
        if (raw) {
          const { result, ts } = JSON.parse(raw);
          if (Date.now() - ts < CACHE_TTL) {
            this.updateAvailBadge(locationId, result);
            return;
          }
        }
      } catch (e) { /* storage unavailable */ }

      try {
        const fmt = d => `${d.getFullYear()}-${d.getMonth() + 1}-${d.getDate()}`;
        const from = new Date();
        const to = new Date(); to.setDate(to.getDate() + 15);
        const url = `${window.locations.api_url}/locations/${locationId}/slots` +
          `?FromDate=${fmt(from)}&ToDate=${fmt(to)}` +
          `&DiaryID=1` +
          `&VisitTypeID=34325378629633` +
          `&optionIDs=9feef0a7-fd61-4735-ab7b-3089a9abed7b` +
          `&optionIDs=17f3be66-02fb-43d6-9178-3b3e7c6caf82` +
          `&optionIDs=2e56edf5-adc3-4eac-9fcd-c5aea45955e2`;

        const controller = new AbortController();
        const timer = setTimeout(() => controller.abort(), 10000); // 10 s per clinic

        let res;
        try {
          res = await fetch(url, { signal: controller.signal });
        } finally {
          clearTimeout(timer);
        }

        if (!res.ok) {
          const body = await res.text().catch(() => '');
          console.warn(`[lv3-avail] ${locationId} error body:`, body.slice(0, 200));
          throw new Error(res.status);
        }
        const data = await res.json();
        const result = this.parseAvailability(data);

        try {
          sessionStorage.setItem(CACHE_KEY, JSON.stringify({ result, ts: Date.now() }));
        } catch (e) { /* sandboxed or full — skip cache */ }

        this.updateAvailBadge(locationId, result);
      } catch (e) {
        console.warn(`[lv3-avail] ${locationId} failed:`, e.message || e);
        // Use 'error' (not 'none') so a network failure renders invisible
        // rather than misleadingly showing "Next appointments in 2+ weeks".
        this.updateAvailBadge(locationId, { status: 'error' });
      }
    }

    // Parse the /locations/{id}/slots response into { status, daysOut? }.
    // Response shape (confirmed from Network tab):
    //   { freeSlots: [{ systemID, locationID, freeSlots: [{ date, timePeriods: [{freeSlots:[]}] }] }] }
    // The outer .freeSlots array contains one clinic response object;
    // that object's own .freeSlots contains the date groups.
    parseAvailability(data) {
      // Unwrap to the clinic response object, handling both the current
      // wrapped shape and the previously seen bare-array shape.
      let clinicResponse;
      if (Array.isArray(data)) {
        clinicResponse = data[0];
      } else if (data && Array.isArray(data.freeSlots)) {
        clinicResponse = data.freeSlots[0];
      } else {
        clinicResponse = data;
      }
      if (!clinicResponse) { return { status: 'none' }; }

      // Sort ascending so we always find the *earliest* available date,
      // regardless of the order the API returns date groups.
      const dateGroups = (clinicResponse.freeSlots || [])
        .slice()
        .sort((a, b) => new Date(a.date) - new Date(b.date));
      if (!dateGroups.length) { return { status: 'none' }; }

      const today = new Date();
      today.setHours(0, 0, 0, 0);

      for (const day of dateGroups) {
        const hasSlots = day.timePeriods
          ? day.timePeriods.some(p => p.freeSlots && p.freeSlots.length > 0)
          : false;
        if (!hasSlots) { continue; }

        const d = new Date(day.date);
        if (isNaN(d)) { continue; }
        d.setHours(0, 0, 0, 0);
        const daysOut = Math.round((d - today) / (1000 * 60 * 60 * 24));
        if (daysOut <= 7)  { return { status: 'soon' }; }
        if (daysOut <= 14) { return { status: 'next_week' }; }
        return { status: 'none' }; // beyond our 15-day window (shouldn't normally occur)
      }

      return { status: 'none' };
    }

    // Swap the placeholder inside [data-lv3-avail] with the resolved badge.
    // The wrapper (.lv3__avail-slot) keeps its min-height so layout never shifts.
    updateAvailBadge(locationId, result) {
      const wrapper = this.querySelector(`[data-lv3-avail="${locationId}"]`);
      if (!wrapper) { return; }

      // Network / parse failure — show a neutral nudge rather than hiding entirely
      if (!result || result.status === 'error') {
        wrapper.innerHTML = `
          <span class="lv3__avail-badge lv3__avail-badge--muted">
            ${window.locations.text.avail_check_error}
          </span>
        `;
        return;
      }

      const calIcon = `<svg width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden="true" xmlns="http://www.w3.org/2000/svg"><rect x="1" y="2" width="10" height="9" rx="1.5" stroke="currentColor" stroke-width="1.2"/><path d="M4 1v2M8 1v2M1 5h10" stroke="currentColor" stroke-width="1.2" stroke-linecap="round"/></svg>`;

      if (result.status === 'soon') {
        // Slots within the next 7 days
        wrapper.innerHTML = `
          <span class="lv3__avail-badge lv3__avail-badge--soon">
            <span class="lv3__avail-dot"></span>
            ${window.locations.text.avail_this_week}
          </span>
        `;
      } else if (result.status === 'next_week') {
        // Slots 8–14 days out
        wrapper.innerHTML = `
          <span class="lv3__avail-badge lv3__avail-badge--later">
            ${calIcon}
            ${window.locations.text.avail_next_week}
          </span>
        `;
      } else {
        // status === 'none': nothing in 15-day window
        wrapper.innerHTML = `
          <span class="lv3__avail-badge lv3__avail-badge--later">
            ${calIcon}
            ${window.locations.text.avail_two_plus_weeks}
          </span>
        `;
      }
    }

    renderSpecialties(item) {
      if (!item.specialties) { return ''; }
      return `
        <div class="lv3__location-badges">
          <span class="lv3__location-badge">${item.specialties}</span>
        </div>
      `;
    }

    renderPreferredClinic(item) {
      if (item.externalId != this.prefLocation || !window.locations.is_customer) { return ''; }
      return `<div class="lv3__location-pref">${window.locations.text.preferred_clinic}</div>`;
    }

    isBonLookOverride(item) {
      const bonLookIds = [4531, 4532, 4533, 4534, 4535, 4536, 4537, 604, 605, 606, 607];
      return bonLookIds.includes(Number(item.externalId));
    }

    // compact=true: minimal styles for map popup / selected card
    renderBookingButton(item, compact = false) {
      if (item.consortium === 'Solis Optics') { return ''; }
      if (this.isBonLookOverride(item)) { return ''; }

      if (item.consortium === 'BonLook') {
        const url = this.getPageUrl(item);
        if (compact) {
          return `<a href="${url}" class="lv3__btn lv3__btn--primary">${window.locations.text.book_appointment}</a>`;
        }
        return `<a href="${url}" class="lv3__btn lv3__btn--primary">${window.locations.text.book_appointment} <svg class="lv3__btn-chevron" width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true"><path d="M2 6h8M8 3l3 3-3 3" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg></a>`;
      }

      const isFyi    = item.consortium.toUpperCase() === 'FYIDOCTORS';
      const enabled  = item.webSchedulerEnabled;
      const clinicId = String(item.externalId);

      if (!isFyi && this.visiqueHiddenBookingIds.includes(clinicId)) { return ''; }

      // FYi without web scheduler → Call clinic (tel:)
      if (isFyi && !enabled) {
        const phone = item.phoneNumber ? item.phoneNumber.replace(/\D/g, '') : '';
        if (!phone) { return ''; }
        const phoneIcon = `<svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true" xmlns="http://www.w3.org/2000/svg"><path d="M12 9.33c-.89-.04-1.76-.19-2.59-.44a.97.97 0 0 0-.99.23l-1.6 1.6A9.33 9.33 0 0 1 2.28 5.18l1.6-1.61a.97.97 0 0 0 .24-.99A9.25 9.25 0 0 1 3.67 2C3.6 1.45 3.11 1 2.55 1H1.33C.72 1 .22 1.5.25 2.11A11 11 0 0 0 11.89 13.75c.61.03 1.11-.47 1.11-1.08v-1.22c0-.56-.45-1.05-1-.12Z" stroke="currentColor" stroke-width="1.2" stroke-linejoin="round"/></svg>`;
        return `<a href="tel:${phone}" class="lv3__btn lv3__btn--primary">${phoneIcon} ${window.locations.text.call_clinic}</a>`;
      }

      // For Visique, French is the default locale (no URL prefix).
      // English is secondary, so English pages are served under /en/.
      // Prefix the booking path with /en/ when the current locale is English.
      const visiqueLangPrefix = (!isFyi && this.currentLocale() === 'en') ? '/en' : '';

      const apptUrl = isFyi
        ? 'https://fyidoctors.com/pages/book-an-appointment?locationId='
        : enabled
          ? `https://visique.com${visiqueLangPrefix}/pages/rendez-vous?locationId=`
          : `https://visique.com${visiqueLangPrefix}/pages/demander-un-rendez-vous?locationId=`;

      let newUrl = apptUrl;
      if (
        window.location.host === 'web.uat.fyidoctors.com' ||
        window.location.host === 'visique-dev.myshopify.com'
      ) {
        const { host } = new URL(apptUrl);
        const newHost = host === 'visique.com' ? 'visique-dev.myshopify.com' : 'web.uat.fyidoctors.com';
        newUrl = apptUrl.replace(host, newHost);
      }

      if (compact) {
        return `<a href="${newUrl}${item.externalId}" class="lv3__btn lv3__btn--primary">${window.locations.text.book_appointment}</a>`;
      }

      return `
        <a href="${newUrl}${item.externalId}" class="lv3__btn lv3__btn--primary">
          ${window.locations.text.book_appointment}
          <svg class="lv3__btn-chevron" width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
            <path d="M2 6h8M8 3l3 3-3 3" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
        </a>
      `;
    }

    appendCurrentParams(url) {
      const currentParams = new URLSearchParams(window.location.search);
      if (!currentParams.toString() || !url || url === '#') { return url; }
      const separator = url.includes('?') ? '&' : '?';
      return `${url}${separator}${currentParams.toString()}`;
    }

    appendUtmSource(url) {
      const utmSource = new URLSearchParams(window.location.search).get('utm_source');
      if (!utmSource || !url || url === '#') { return url; }
      const separator = url.includes('?') ? '&' : '?';
      return `${url}${separator}utm_source=${encodeURIComponent(utmSource)}`;
    }

    renderDetailsButton(item, compact = false) {
      if (item.consortium === 'Solis Optics' && item.webpageEn === null) {
        const solisUrl = this.appendCurrentParams('https://www.solisoptics.ca/');
        return `<a href="${solisUrl}" class="lv3__btn lv3__btn--outline">${window.locations.text.view_details}${compact ? '' : ' <svg class="lv3__btn-chevron" width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true"><path d="M2 6h8M8 3l3 3-3 3" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>'}</a>`;
      }

      if (this.isBonLookOverride(item)) {
        const bonlookUrl = this.appendCurrentParams('https://bonlook.ca');
        return `<a href="${bonlookUrl}" class="lv3__btn lv3__btn--outline">${window.locations.text.view_details}</a>`;
      }

      const webpage = this.getPageUrl(item);

      if (
        window.location.host === 'web.uat.fyidoctors.com' ||
        window.location.host === 'visique-dev.myshopify.com'
      ) {
        try {
          const { host } = new URL(webpage);
          const newHost = host === 'visique.com' ? 'visique-dev.myshopify.com' : 'web.uat.fyidoctors.com';
          const newUrl = this.appendUtmSource(webpage.replace(host, newHost));
          return `<a href="${newUrl}" class="lv3__btn lv3__btn--outline">${window.locations.text.view_details}${compact ? '' : ' <svg class="lv3__btn-chevron" width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true"><path d="M2 6h8M8 3l3 3-3 3" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>'}</a>`;
        } catch (e) {
          // fall through
        }
      }
      if (!webpage) { return ''; }
      return `<a href="${this.appendUtmSource(webpage)}" class="lv3__btn lv3__btn--outline">${window.locations.text.view_details}${compact ? '' : ' <svg class="lv3__btn-chevron" width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true"><path d="M2 6h8M8 3l3 3-3 3" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>'}</a>`;
    }

    renderMakePreferred(item) {
      if (!window.locations.is_customer || item.externalId === Number(this.prefLocation)) { return ''; }
      return `
        <location-pref-button-v3 class="lv3__make-preferred-wrapper" data-location-id="${item.externalId}">
          <button type="button" class="lv3__make-preferred" data-prefer-this-location>
            ${window.locations.text.make_preferred_clinic}
          </button>
        </location-pref-button-v3>
      `;
    }

    // ------------------------------------------------------------------
    // Filtering — 50 km radius from map centre, exclude Solis/BonLook
    // ------------------------------------------------------------------
    filterLocations() {
      const center = this.map.getCenter();
      let locations = this.allLocations;

      locations = locations.filter(item =>
        item.consortium !== 'Solis Optics' && item.consortium !== 'BonLook'
      );

      if (!center) { return locations; }
      return locations.filter(item =>
        this.calcCrow(center.lat(), center.lng(), item.latitude, item.longitude) <= this.range / 1000
      );
    }

    // ------------------------------------------------------------------
    // Preferred clinic: API POST + DOM update
    // ------------------------------------------------------------------
    updatePreferredClinic(locationID, parentEl) {
      const token = localStorage.getItem('LRTokenKey');
      const body = JSON.stringify({ locationId: locationID });

      fetch(`${window.locations.api_url}/customer/preferred-clinic`, {
        method: 'POST',
        headers: {
          'Authorization': 'Bearer ' + token,
          'Content-Type': 'application/json; charset=utf-8'
        },
        body: body
      })
        .then(response => {
          if (response.status === 200) { return response.json(); }
          throw new Error('Request failed');
        })
        .then(result => {
          const data = result.locationId;
          localStorage.setItem('pc', data);
          this.prefLocation = data;
          this.removeOldPreferredLocationEl();
          this.updatePreferredLocationEl(locationID, parentEl);
          this.renderMarkers();
          setTimeout(() => { this.handleOptionalRedirect(); }, 50);
        })
        .catch(error => console.error(error));
    }

    removeOldPreferredLocationEl() {
      const oldPrefLocation = [...this.querySelectorAll('[data-lv3-location]')]
        .find(location => location.classList.contains('lv3__location--preferred'));
      if (!oldPrefLocation) { return; }

      const item = this.allLocations.find(
        location => location.externalId === Number(oldPrefLocation.dataset.locId)
      );
      const prefEl = oldPrefLocation.querySelector('[data-lv3-pref-status]');

      oldPrefLocation.classList.toggle('lv3__location--preferred', item.externalId === Number(this.prefLocation));
      prefEl.innerHTML = `${this.renderPreferredClinic(item)}${this.renderMakePreferred(item)}`;
    }

    updatePreferredLocationEl(locationID, parentEl) {
      const item = this.allLocations.find(location => location.externalId === Number(locationID));
      const prefEl = parentEl.querySelector('[data-lv3-pref-status]');

      prefEl.innerHTML = `${this.renderPreferredClinic(item)}${this.renderMakePreferred(item)}`;
      parentEl.classList.toggle('lv3__location--preferred', item.externalId === Number(this.prefLocation));
    }

    handleOptionalRedirect() {
      const urlParams = new URLSearchParams(window.location.search);
      const r = urlParams.get('continueTo');
      if (r) {
        setTimeout(() => {
          window.location.href = r.replace('%2F', '?').replace('%3D', '=');
        }, 1000);
      }
    }

    // ------------------------------------------------------------------
    // Haversine
    // ------------------------------------------------------------------
    toRad(value) {
      return value * Math.PI / 180;
    }

    calcCrow(lat1, lon1, lat2, lon2) {
      const R = 6371;
      const dLat = this.toRad(lat2 - lat1);
      const dLon = this.toRad(lon2 - lon1);
      const rlat1 = this.toRad(lat1);
      const rlat2 = this.toRad(lat2);
      const a = Math.sin(dLat / 2) * Math.sin(dLat / 2) +
        Math.sin(dLon / 2) * Math.sin(dLon / 2) * Math.cos(rlat1) * Math.cos(rlat2);
      const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
      return R * c;
    }

  }); // end define locations-v3-section

})();


// --------------------------------------------------------------------
// Preferred Clinic Button V3
// --------------------------------------------------------------------
(() => {
  if (typeof window.customElements.get('location-pref-button-v3') !== 'undefined') { return; }

  window.customElements.define('location-pref-button-v3', class LocationsPrefButtonV3 extends HTMLElement {
    constructor() {
      super();
      this.button     = this.querySelector('[data-prefer-this-location]');
      this.parentList = this.closest('locations-v3-section');
      this.init();
    }

    init() {
      if (!this.button || !this.parentList) { return; }
      this.button.addEventListener('click', () => {
        const parentEl = this.closest('[data-lv3-location]');
        this.parentList.updatePreferredClinic(this.dataset.locationId, parentEl);
      });
    }
  });

})();
